/**
 * WhatsApp Database Service
 *
 * Service per caricare messaggi WhatsApp dal database Supabase.
 * Consente caricamento istantaneo (~50ms) invece che da API WAHA (~500ms).
 *
 * Funzionalità:
 * - Carica messaggi dal DB con filtri temporali
 * - Carica reactions associate ai messaggi
 * - Trova timestamp più vecchio per determinare limite DB
 * - Gestisce formati JID diversi (individuale @c.us, gruppo @g.us)
 * - Trasforma record DB in Message type
 */

import { supabase } from '../supabase';
import type { Message, MessageReaction, ChatOverview, MessageType } from '../types/whatsappChat';
import { decryptWhatsAppMessage, isEncrypted } from '../utils/crypto';
import { debugLog } from '../../components/whatsapp2/chat/ChatDebugPanel';
import { parseReplyTo } from '@/lib/utils/whatsappReplyTo';
import { isPlaceholderContactName } from '@/lib/utils/contactNamePlaceholder';
import { dbMessageStatus, normalizeDbMessageType, rowExtras, statusToAck } from '@/lib/services/whatsapp/messageRowMapping';

export class WhatsAppDbService {
  // Cache delle liste di riferimento per il matching nomi (lookupContactNames):
  // ogni lookup scaricava TUTTA l'anagrafica dell'org per fare il match
  // client-side. TTL breve + dedup delle richieste in volo.
  private static refListCache = new Map<string, { promise: Promise<any[]>; ts: number }>();
  private static readonly REF_LIST_TTL = 60_000;

  private static cachedRefList(key: string, fetcher: () => Promise<any[]>): Promise<any[]> {
    const cached = this.refListCache.get(key);
    if (cached && Date.now() - cached.ts < this.REF_LIST_TTL) return cached.promise;
    const promise = fetcher();
    this.refListCache.set(key, { promise, ts: Date.now() });
    promise.catch(() => this.refListCache.delete(key));
    return promise;
  }

  /**
   * Normalizza un numero di telefono rimuovendo caratteri non numerici
   * e gestendo vari formati (con/senza +, spazi, etc.)
   * @param phone - Numero in qualsiasi formato
   * @returns Solo cifre (es: "393331234567")
   */
  private static normalizePhone(phone: string | null | undefined): string {
    if (!phone) return '';
    // Rimuovi tutto tranne le cifre
    return phone.replace(/\D/g, '');
  }

  /**
   * Estrae il numero dal JID WhatsApp
   * @param jid - JID (es: "393331234567@c.us" o "172013608026319@lid")
   * @returns Solo le cifre prima di @
   */
  private static extractPhoneFromJid(jid: string): string {
    if (!jid) return '';
    const parts = jid.split('@');
    return parts[0] || '';
  }

  /**
   * Cerca nomi dei contatti dato un array di JIDs
   * Cerca in ordine: client_contacts.phone, clients.phone, clients.whatsapp_number
   *
   * @param jids - Array di JIDs (es: ["393331234567@c.us", "172013608026319@lid"])
   * @param organizationId - ID organizzazione per filtrare i contatti
   * @returns Mappa JID → nome (solo per i JID trovati)
   */
  static async lookupContactNames(
    jids: string[],
    organizationId: string
  ): Promise<Map<string, string>> {
    const result = new Map<string, string>();
    if (!jids.length || !organizationId) return result;

    // Estrai numeri unici dai JIDs (filtra valori non stringa)
    const phoneToJid = new Map<string, string>();
    for (const jid of jids) {
      // Salta valori non stringa (potrebbe essere un oggetto o undefined)
      if (typeof jid !== 'string') continue;
      const phone = this.extractPhoneFromJid(jid);
      if (phone && /^\d+$/.test(phone)) {
        phoneToJid.set(phone, jid);
      }
    }

    const phones = Array.from(phoneToJid.keys());
    if (!phones.length) return result;

    console.log('[DbService] lookupContactNames:', {
      jidsCount: jids.length,
      uniquePhones: phones.length,
      samplePhones: phones.slice(0, 3),
    });

    // Cerca in client_contacts
    const contacts = await this.cachedRefList(`contacts_${organizationId}`, async () => {
      const { data } = await supabase
        .from('client_contacts')
        .select('name, phone')
        .eq('organization_id', organizationId)
        .not('phone', 'is', null);
      return data || [];
    }).catch(() => [] as any[]);

    if (contacts) {
      for (const contact of contacts) {
        const normalizedContactPhone = this.normalizePhone(contact.phone);
        // Nomi segnaposto ("Referente WhatsApp", creati alla firma dai numeri del gruppo)
        // non sono il nome di una persona: meglio il cliente, il profilo WhatsApp o il numero.
        // Numeri troppo corti farebbero "match" con qualunque chat.
        if (isPlaceholderContactName(contact.name) || normalizedContactPhone.length < 8) continue;
        // Verifica se il numero (normalizzato) è nella lista
        for (const phone of phones) {
          // Match se il numero del contatto contiene il numero cercato o viceversa
          if (normalizedContactPhone.endsWith(phone) || phone.endsWith(normalizedContactPhone)) {
            const jid = phoneToJid.get(phone);
            if (jid && contact.name && !result.has(jid)) {
              result.set(jid, contact.name);
            }
          }
        }
      }
    }

    // Cerca in clients (Lead e Clienti, persone e aziende) per chi non e' gia' stato trovato.
    // Si chiedono al database solo i numeri delle chat (colonna phone_digits = prefisso + numero,
    // solo cifre): scaricare tutta l'anagrafica si fermava alle prime 1000 righe e nelle
    // organizzazioni grandi (Obly ne ha ~71.000 con un numero) quasi nessun nome veniva trovato.
    const remainingPhones = phones.filter((p) => !result.has(phoneToJid.get(p)!));
    if (remainingPhones.length > 0) {
      // Varianti salvate: con prefisso 39 e senza (numero italiano scritto senza country_code).
      const variantToPhone = new Map<string, string>();
      for (const phone of remainingPhones) {
        variantToPhone.set(phone, phone);
        if (phone.startsWith('39') && phone.length > 10) variantToPhone.set(phone.slice(2), phone);
      }
      const variants = [...variantToPhone.keys()];
      for (let i = 0; i < variants.length; i += 100) {
        const chunk = variants.slice(i, i + 100);
        const list = chunk.join(',');
        const { data: clients } = await supabase
          .from('clients')
          .select('business_name, phone_digits, whatsapp_number')
          .eq('organization_id', organizationId)
          .is('deleted_at', null)
          .or(`phone_digits.in.(${list}),whatsapp_number.in.(${list})`)
          .limit(1000);
        for (const client of clients || []) {
          if (!client.business_name || isPlaceholderContactName(client.business_name)) continue;
          for (const value of [client.phone_digits, this.normalizePhone(client.whatsapp_number)]) {
            const phone = value ? variantToPhone.get(value) : undefined;
            const jid = phone ? phoneToJid.get(phone) : undefined;
            if (jid && !result.has(jid)) result.set(jid, client.business_name);
          }
        }
      }
    }

    // Cerca in user_profiles (utenti CRM dell'organizzazione)
    const remainingPhones2 = phones.filter((p) => !result.has(phoneToJid.get(p)!));
    if (remainingPhones2.length > 0) {
      const users = await this.cachedRefList(`users_${organizationId}`, async () => {
        const { data } = await supabase
          .from('user_profiles')
          .select('full_name, phone')
          .eq('organization_id', organizationId)
          .not('phone', 'is', null);
        return data || [];
      }).catch(() => [] as any[]);

      if (users) {
        for (const user of users) {
          const normalizedPhone = this.normalizePhone(user.phone);

          for (const phone of remainingPhones2) {
            const jid = phoneToJid.get(phone);
            if (!jid || result.has(jid)) continue;

            if (
              normalizedPhone &&
              (normalizedPhone.endsWith(phone) || phone.endsWith(normalizedPhone))
            ) {
              if (user.full_name) {
                result.set(jid, user.full_name);
              }
            }
          }
        }
      }
    }

    // Cerca nei contatti importati da Google (Rubrica). La RLS mostra a ciascun utente
    // solo i contatti che ha importato lui: il nome da Google lo vede solo chi lo ha.
    const remainingPhones3 = phones.filter((p) => !result.has(phoneToJid.get(p)!));
    if (remainingPhones3.length > 0) {
      const googleContacts = await this.cachedRefList(`google_${organizationId}`, async () => {
        const { data } = await supabase
          .from('google_contacts')
          .select('name, given_name, family_name, phone')
          .eq('organization_id', organizationId)
          .not('phone', 'is', null)
          .limit(5000);
        return data || [];
      }).catch(() => [] as any[]);

      for (const gc of googleContacts) {
        const name = gc.name || [gc.given_name, gc.family_name].filter(Boolean).join(' ');
        const normalizedPhone = this.normalizePhone(gc.phone);
        if (!name || normalizedPhone.length < 8) continue;
        for (const phone of remainingPhones3) {
          const jid = phoneToJid.get(phone);
          if (!jid || result.has(jid)) continue;
          if (normalizedPhone.endsWith(phone) || phone.endsWith(normalizedPhone)) result.set(jid, name);
        }
      }
    }

    console.log('[DbService] lookupContactNames result:', {
      found: result.size,
      sample: Array.from(result.entries()).slice(0, 3),
    });

    return result;
  }

  /**
   * Carica messaggi dal database
   *
   * @param configId - ID configurazione WAHA
   * @param chatId - JID della chat (es. 393123456789@c.us)
   * @param options - Opzioni di caricamento (limit, beforeTimestamp)
   * @returns Array di messaggi ordinati per timestamp decrescente
   */
  static async loadMessagesFromDb(
    configId: string,
    chatId: string,
    options: {
      limit?: number;
      beforeTimestamp?: number; // milliseconds
      organizationId?: string; // Per decryption
    } = {}
  ): Promise<Message[]> {
    const limit = options.limit || 50;
    const organizationId = options.organizationId;

    // Rimuovi suffissi JID per matching to_jid
    // DB salva to_jid senza @c.us/@g.us, quindi dobbiamo normalizzare
    const chatIdWithoutSuffix = chatId
      .replace('@c.us', '')
      .replace('@g.us', '')
      .replace('@newsletter', '')
      .replace('@lid', '');

    let query = supabase
      .from('whatsapp_messages')
      .select('*')
      .eq('config_id', configId)
      // Matcha sia messaggi inbound (from_jid) che outbound (to_jid senza suffisso)
      .or(`from_jid.eq.${chatId},to_jid.eq.${chatIdWithoutSuffix}`)
      .order('created_at', { ascending: false }) // Più recenti prima
      .limit(limit);

    // Filtra per timestamp se specificato (per infinite scroll)
    if (options.beforeTimestamp) {
      const beforeDate = new Date(options.beforeTimestamp).toISOString();
      query = query.lt('created_at', beforeDate);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[DbService] Error loading messages:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Filtra messaggi di sistema (hanno body vuoto e non sono messaggi utente)
    // Filtra messaggi di sistema (non sono messaggi utente)
    const SYSTEM_MESSAGE_TYPES = [
      'e2e_notification', 'notification_template', 'protocol',
      'unknown', 'gp2', 'call_log', 'notification',
    ];

    const filteredData = data.filter((record) => {
      // Controlla sia raw_payload._data.type che raw_payload.type che il campo type diretto
      // Le righe chiamata scritte dal core (metadata.kind 'call', dal 26/09) hanno un testo e si mostrano.
      if (record.metadata?.kind === 'call') return true;
      const rawType = record.raw_payload?._data?.type || record.raw_payload?.payload?.type || record.raw_payload?.type || record.type;
      return !SYSTEM_MESSAGE_TYPES.includes(rawType);
    });

    if (filteredData.length === 0) {
      return [];
    }

    // Get all message IDs to batch-load reactions
    const messageIds = filteredData.map((r) => r.id);

    // Load reactions for all messages in one query
    const { data: reactionsData } = await supabase
      .from('whatsapp_message_reactions')
      .select('message_id, reaction, sender_jid')
      .in('message_id', messageIds);

    // Group reactions by message_id
    const reactionsByMessage = new Map<string, MessageReaction[]>();
    if (reactionsData) {
      for (const r of reactionsData) {
        const existing = reactionsByMessage.get(r.message_id);
        if (existing) {
          // Find if we already have this emoji
          const emojiReaction = existing.find((e) => e.emoji === r.reaction);
          if (emojiReaction) {
            emojiReaction.count++;
            emojiReaction.senderJids.push(r.sender_jid);
          } else {
            existing.push({ emoji: r.reaction, count: 1, senderJids: [r.sender_jid] });
          }
        } else {
          reactionsByMessage.set(r.message_id, [{ emoji: r.reaction, count: 1, senderJids: [r.sender_jid] }]);
        }
      }
    }

    // Transform records with reactions attached
    return Promise.all(
      filteredData.map((record) => this.transformDbRecordToMessage(record, organizationId, reactionsByMessage.get(record.id)))
    );
  }

  /**
   * Ottiene timestamp del messaggio più vecchio nel DB per questa chat
   *
   * Usato per determinare quando passare da DB a WAHA API durante scroll.
   * Se oldestTimestamp = 15 dic 2024, significa che messaggi prima del 15 dic
   * non sono nel DB e vanno caricati da WAHA.
   *
   * @param configId - ID configurazione WAHA
   * @param chatId - JID della chat
   * @returns Timestamp in millisecondi del messaggio più vecchio, o null se nessun messaggio
   */
  static async getOldestMessageTimestamp(
    configId: string,
    chatId: string
  ): Promise<number | null> {
    const chatIdWithoutSuffix = chatId
      .replace('@c.us', '')
      .replace('@g.us', '')
      .replace('@newsletter', '');

    const { data, error } = await supabase
      .from('whatsapp_messages')
      .select('created_at')
      .eq('config_id', configId)
      .or(`from_jid.eq.${chatId},to_jid.eq.${chatIdWithoutSuffix}`)
      .order('created_at', { ascending: true }) // ASC = più vecchio
      .limit(1);

    if (error || !data || data.length === 0) {
      return null;
    }

    return new Date(data[0].created_at).getTime();
  }

  /**
   * CLOUD — Carica messaggi di una chat per provider Cloud (Standalone/Coex).
   *
   * I messaggi Cloud sono inseriti dal webhook meta-whatsapp-webhook con
   * `chat_identifier_e164` = numero E.164 del cliente (es. "393278787927").
   * Matchiamo su quella colonna invece che su from_jid/to_jid: il Cloud salva
   * to_jid CON suffisso @c.us mentre WAHA lo salva senza → matchare su E.164 è
   * l'unico modo robusto cross-direzione.
   *
   * @param configId - ID config Cloud
   * @param e164 - Numero E.164 puro (senza + né @c.us)
   */
  static async loadCloudMessagesFromDb(
    configId: string,
    e164: string,
    options: { limit?: number; beforeTimestamp?: number; organizationId?: string } = {}
  ): Promise<Message[]> {
    const limit = options.limit || 50;
    let query = supabase
      .from('whatsapp_messages')
      .select('*')
      .eq('config_id', configId)
      .eq('chat_identifier_e164', e164)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (options.beforeTimestamp) {
      query = query.lt('created_at', new Date(options.beforeTimestamp).toISOString());
    }

    const { data, error } = await query;
    if (error) {
      console.error('[DbService] loadCloudMessagesFromDb error:', error);
      throw error;
    }
    if (!data || data.length === 0) return [];

    const SYSTEM_MESSAGE_TYPES = [
      'e2e_notification', 'notification_template', 'protocol',
      'unknown', 'gp2', 'call_log', 'notification',
    ];
    const filteredData = data.filter((r) => r.metadata?.kind === 'call' || !SYSTEM_MESSAGE_TYPES.includes(r.type));
    if (filteredData.length === 0) return [];

    const messageIds = filteredData.map((r) => r.id);
    const { data: reactionsData } = await supabase
      .from('whatsapp_message_reactions')
      .select('message_id, reaction, sender_jid')
      .in('message_id', messageIds);

    const reactionsByMessage = new Map<string, MessageReaction[]>();
    if (reactionsData) {
      for (const r of reactionsData) {
        const existing = reactionsByMessage.get(r.message_id);
        if (existing) {
          const emojiReaction = existing.find((e) => e.emoji === r.reaction);
          if (emojiReaction) {
            emojiReaction.count++;
            emojiReaction.senderJids.push(r.sender_jid);
          } else {
            existing.push({ emoji: r.reaction, count: 1, senderJids: [r.sender_jid] });
          }
        } else {
          reactionsByMessage.set(r.message_id, [{ emoji: r.reaction, count: 1, senderJids: [r.sender_jid] }]);
        }
      }
    }

    return Promise.all(
      filteredData.map((record) =>
        this.transformDbRecordToMessage(record, options.organizationId, reactionsByMessage.get(record.id))
      )
    );
  }

  /**
   * CLOUD — Costruisce la lista chat per un numero Cloud aggregando i messaggi
   * in DB per `chat_identifier_e164`. Niente API polling (CloudProvider.getChats=[]):
   * la fonte è whatsapp_messages popolata dal webhook.
   *
   * Strategia MVP: prende gli ultimi N messaggi della config (ordine desc), raggruppa
   * per E.164 tenendo il primo (= più recente) come lastMessage. Arricchisce i nomi
   * dai contatti CRM. Per volumi alti si passerà a una RPC con GROUP BY lato DB.
   */
  static async loadCloudChatsFromDb(
    configId: string,
    organizationId: string,
    limit = 1000
  ): Promise<ChatOverview[]> {
    const { data, error } = await supabase
      .from('whatsapp_messages')
      .select('chat_identifier_e164, content, type, direction, from_me, created_at, status')
      .eq('config_id', configId)
      .not('chat_identifier_e164', 'is', null)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('[DbService] loadCloudChatsFromDb error:', error);
      throw error;
    }
    if (!data || data.length === 0) return [];

    const byChat = new Map<string, ChatOverview>();
    for (const r of data) {
      const e164 = r.chat_identifier_e164 as string | null;
      if (!e164 || byChat.has(e164)) continue; // primo = più recente (ordine desc)
      byChat.set(e164, {
        id: `${e164}@c.us`,
        name: `+${e164}`,
        isGroup: false,
        pinned: false,
        archived: false,
        unreadCount: 0,
        lastMessage: {
          body: r.content || '',
          // In secondi, come WAHA: la lista moltiplica per 1000 (prima tutte le chat Ufficiali dicevano "adesso").
          timestamp: Math.floor(new Date(r.created_at).getTime() / 1000),
          fromMe: !!r.from_me,
          type: normalizeDbMessageType(r.type) as MessageType,
          // Spunte vere in lista per l'ultimo messaggio inviato (colonna status).
          ...(r.from_me && statusToAck(r.status) ? { ack: statusToAck(r.status) } : {}),
        },
      });
    }

    const overviews = Array.from(byChat.values());

    // Arricchisci nomi dai contatti/clienti CRM
    try {
      const names = await this.lookupContactNames(overviews.map((o) => o.id), organizationId);
      if (names.size > 0) {
        for (const o of overviews) {
          const n = names.get(o.id);
          if (n) o.name = n;
        }
      }
    } catch {
      /* best-effort: lascia il numero come nome */
    }

    return overviews.sort(
      (a, b) => (b.lastMessage?.timestamp || 0) - (a.lastMessage?.timestamp || 0)
    );
  }

  /**
   * Trasforma record DB in Message type con decryption
   *
   * @param record - Record dal database
   * @param organizationId - ID organizzazione per decryption
   * @param reactions - Reactions già aggregate per questo messaggio
   * @returns Message object con contenuto decifrato
   */
  private static async transformDbRecordToMessage(
    record: any,
    organizationId?: string,
    reactions?: MessageReaction[]
  ): Promise<Message> {
    // Determina chatId corretto per il messaggio
    let chatId: string;
    if (record.direction === 'inbound') {
      chatId = record.from_jid;
    } else {
      if (record.to_jid) {
        chatId = record.to_jid.includes('@') ? record.to_jid : `${record.to_jid}@c.us`;
      } else {
        chatId = record.from_jid || 'unknown';
      }
    }

    // Decifra content e reply_to_body se criptati
    let content = record.content || '';
    const parsedReply = parseReplyTo(record.reply_to_id, record.reply_to_body);
    let replyToBody = parsedReply.replyToBody;

    // Tipi che NON richiedono decrittazione del content (messaggi media)
    const MEDIA_TYPES = ['audio', 'ptt', 'voice', 'image', 'video', 'sticker', 'document', 'location', 'contact'];

    // Un messaggio è media se:
    // 1. Il tipo è nella lista MEDIA_TYPES, oppure
    // 2. Ha un media_url (indica che è un messaggio con media), oppure
    // 3. Ha un media_mimetype che indica audio/video/image
    const hasMediaType = MEDIA_TYPES.includes(record.type);
    const hasMediaUrl = !!record.media_url;
    const hasMediaMimetype = record.media_mimetype && (
      record.media_mimetype.startsWith('audio/') ||
      record.media_mimetype.startsWith('video/') ||
      record.media_mimetype.startsWith('image/') ||
      record.media_mimetype.startsWith('application/')
    );
    const isMediaMessage = hasMediaType || hasMediaUrl || hasMediaMimetype;

    try {
      // Decifra content solo se sembra criptato E non è un messaggio media
      if (content && organizationId && !isMediaMessage && isEncrypted(content)) {
        content = await decryptWhatsAppMessage(content, organizationId);
      }
      // Decifra reply_to_body solo se sembra criptato
      if (replyToBody && organizationId && isEncrypted(replyToBody)) {
        replyToBody = await decryptWhatsAppMessage(replyToBody, organizationId);
      }
    } catch (error) {
      console.error('[DbService] Decryption error:', record.id);
    }

    // Per gruppi: il sender è in raw_payload.payload.author o raw_payload.payload._data.author
    // Per chat 1:1: il sender è from_jid
    // NOTA: raw_payload è il body completo del webhook: { event, session, payload: { ...messaggio } }
    // Il messaggio effettivo è in raw_payload.payload
    const isGroupMessage = record.from_jid?.endsWith('@g.us') || record.to_jid?.endsWith('@g.us');
    let senderJid: string | undefined;

    if (record.direction === 'inbound') {
      if (isGroupMessage) {
        // Estrai sender dal raw_payload per messaggi di gruppo
        // NOTA: author/_data.author può essere stringa o oggetto con _serialized
        const rawPayload = record.raw_payload;
        const messagePayload = rawPayload?.payload || rawPayload;
        const extractJid = (val: any): string | undefined => {
          if (!val) return undefined;
          if (typeof val === 'string') return val;
          if (typeof val === 'object' && val._serialized) return val._serialized;
          return undefined;
        };

        senderJid =
          extractJid(messagePayload?.author) ||
          extractJid(messagePayload?._data?.author) ||
          extractJid(messagePayload?.participant) ||
          extractJid(messagePayload?._data?.participant) ||
          extractJid(messagePayload?._data?.key?.participant);

        // Debug per gruppi
        if (!senderJid) {
          debugLog('DbService', `⚠️ Group msg SENZA senderJid: ${record.id?.slice(0,20)}`, {
            from_jid: record.from_jid,
            'payload.author': messagePayload?.author,
            'payload._data?.author': messagePayload?._data?.author,
            'payload.participant': messagePayload?.participant,
            'payload._data?.participant': messagePayload?._data?.participant,
            'payload._data?.key?.participant': messagePayload?._data?.key?.participant,
            payloadKeys: messagePayload ? Object.keys(messagePayload) : [],
            dataKeys: messagePayload?._data ? Object.keys(messagePayload._data) : [],
          }, 'warn');
        }
      } else {
        // Per chat 1:1, il sender è from_jid
        senderJid = record.from_jid;
      }
    }

    // Estrai sender name da raw_payload.payload (notifyName/pushName da WAHA NOWEB)
    let senderName: string | undefined;
    if (isGroupMessage && record.direction === 'inbound' && record.raw_payload) {
      const rawPayload = record.raw_payload;
      const messagePayload = rawPayload?.payload || rawPayload;
      senderName = messagePayload?.notifyName ||
                   messagePayload?._data?.notifyName ||
                   messagePayload?.pushName ||
                   messagePayload?._data?.pushName ||
                   messagePayload?._data?.key?.pushName ||
                   undefined;
    }

    // Inferisci sticker da mimetype per record DB storici salvati come 'text'
    let type = normalizeDbMessageType(record.type) as MessageType;
    if (type === 'text' && record.media_mimetype === 'image/webp') {
      type = 'sticker';
    }

    return {
      id: record.id,
      chatId,
      content,
      type,
      direction: record.direction,
      // Le righe dei numeri Ufficiale non hanno ack_status: lo stato e' in status.
      status: dbMessageStatus(record),
      timestamp: new Date(record.created_at),
      // Placeholder `meta:<id>` = media Cloud non ancora scaricato dal cron → UI mostra
      // "in caricamento" invece di un URL rotto. Verrà sostituito con l'URL storage.
      mediaUrl: typeof record.media_url === 'string' && record.media_url.startsWith('meta:')
        ? undefined
        : record.media_url,
      mediaMimetype: record.media_mimetype,
      mediaFilename: record.media_filename,
      replyToId: parsedReply.replyToId,
      replyToBody,
      senderJid,
      senderName,
      isEdited: record.is_edited,
      isDeleted: record.is_deleted,
      reactions,
      transcription: record.transcription || undefined,
      ...rowExtras(record),
    };
  }
}
